<template>
    <div class="cliente-panel-wrapper mb-3 mt-1">
        <div class="d-flex align-center">
            <v-autocomplete
                v-model="clienteSeleccionadoId"
                :items="displayResults"
                :loading="isSearching"
                :search-input.sync="searchText"
                item-text="displayName"
                item-value="id"
                placeholder="Buscar cliente por documento o nombre..."
                outlined
                dense
                hide-details
                no-filter
                hide-no-data
                prepend-inner-icon="mdi-account-search"
                @change="onClientSelect"
                @click:clear="onClear"
                background-color="white"
            >
                <template v-slot:selection="{ item }">
                    <span class="font-weight-medium primary--text">{{ item.defaultName || item.name }}</span>
                    <span class="ml-2 text-muted caption">({{ item.number }})</span>
                </template>
                <template v-slot:item="{ item }">
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle
                            >{{ item.number }} <span v-if="item.email">- {{ item.email }}</span></v-list-item-subtitle
                        >
                    </v-list-item-content>
                </template>
            </v-autocomplete>

            <v-btn v-if="!clienteSeleccionadoId" color="primary" class="ml-2" @click="abrirModal('crear')" depressed height="40">
                <v-icon left>mdi-plus</v-icon> Crear
            </v-btn>
            <v-btn
                v-else
                color="primary"
                outlined
                class="ml-2"
                @click="abrirModal('editar')"
                height="40"
                title="Editar datos del cliente"
                :loading="isSearching && !clienteActual"
                :disabled="isSearching && !clienteActual"
            >
                <v-icon left>mdi-pencil</v-icon> Editar
            </v-btn>
            <v-btn v-if="clienteSeleccionadoId" color="red" outlined class="ml-2" @click="onClear" height="40" title="Deseleccionar cliente">
                <v-icon left>mdi-account-remove</v-icon> Deseleccionar
            </v-btn>
        </div>

        <!-- MODAL CREAR/EDITAR CLIENTE -->
        <v-dialog v-model="modalVisible" max-width="800px" persistent scrollable>
            <v-card>
                <v-card-title class="primary white--text py-3">
                    <span class="text-h6">{{ modoModal === 'crear' ? `Crear Nuevo ${tipoLabel}` : `Editar ${tipoLabel}` }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="cerrarModal"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pt-5">
                    <form @submit.prevent="modoModal === 'crear' ? guardarClienteNuevo() : guardarCliente()">
                        <v-row dense>
                            <v-col cols="12" sm="4" md="3">
                                <v-autocomplete
                                    dense
                                    outlined
                                    v-model="formActivo.idTipoDocumento"
                                    v-validate="'required'"
                                    :items="tipoDocumento"
                                    item-text="text"
                                    item-value="id"
                                    :error-messages="errors.collect('formActivo.idTipoDocumento')"
                                    label="Tipo documento"
                                    data-vv-name="formActivo.idTipoDocumento"
                                    required
                                    :disabled="modoModal === 'editar'"
                                    :readonly="modoModal === 'editar'"
                                />
                            </v-col>
                            <v-col cols="12" sm="8" md="4">
                                <v-text-field
                                    dense
                                    outlined
                                    v-model="formActivo.numeroDocumento"
                                    v-validate="docValidationRule"
                                    :error-messages="errors.collect('formActivo.numeroDocumento')"
                                    :label="docLabel"
                                    :hint="docHint"
                                    persistent-hint
                                    data-vv-name="formActivo.numeroDocumento"
                                    type="tel"
                                    :maxlength="docMaxLength"
                                    :readonly="modoModal === 'editar'"
                                    @keypress="soloNumeros"
                                    :loading="buscandoDoc"
                                >
                                    <template v-slot:append>
                                        <v-icon @click="buscarDocumento" color="primary">mdi-magnify</v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    dense
                                    outlined
                                    v-model="formActivo.full_name"
                                    v-validate="'required'"
                                    :error-messages="errors.collect('formActivo.full_name')"
                                    label="Razón Social / Nombres"
                                    data-vv-name="formActivo.full_name"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field dense outlined v-model="formActivo.telefono" label="Teléfono" type="tel" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field dense outlined v-model="formActivo.correo" label="Email" type="email" />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined v-model="formActivo.direccion" label="Dirección" />
                            </v-col>
                            <v-col cols="12">
                                <SelectUbigeo v-model="formActivo.isDistrito" label="Distrito" outlined dense />
                            </v-col>
                        </v-row>
                        <div class="d-flex justify-end mt-4">
                            <v-btn text @click="cerrarModal" class="mr-2">Cancelar</v-btn>
                            <v-btn color="primary" depressed type="submit" :loading="loadingSave"> Guardar </v-btn>
                        </div>
                    </form>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import DefaultForm from '@/utils/defaultForm'
import { debounce } from 'lodash'
import SelectUbigeo from '@/components/selects/SelectUbigeo'

export default {
    $_veeValidate: { validator: 'new' },
    components: { SelectUbigeo },
    props: {
        value: {
            type: [Number, String],
            default: null
        },
        type: {
            type: String,
            default: 'customers', // 'customers' or 'suppliers'
            validator: (v) => ['customers', 'suppliers'].includes(v)
        }
    },
    data() {
        return {
            clienteSeleccionadoId: null,
            clienteActual: null,
            searchText: '',
            searchResults: [],
            isSearching: false,

            modalVisible: false,
            modoModal: 'crear',
            formActivo: {},
            loadingSave: false,
            buscandoDoc: false
        }
    },
    computed: {
        ...mapState('utilities', ['tipoDocumento']),
        ...mapState('users', ['user']),
        tipoLabel() {
            return this.type === 'suppliers' ? 'Proveedor' : 'Cliente'
        },
        displayResults() {
            const results = [...this.searchResults]
            if (this.clienteActual && !results.find((r) => r.id === this.clienteActual.id)) {
                results.unshift(this.clienteActual)
            }
            return results
        },
        // — Document validation helpers —
        isDNI() {
            return this.formActivo.idTipoDocumento === '1'
        },
        isRUC() {
            return this.formActivo.idTipoDocumento === '6'
        },
        docMaxLength() {
            if (this.isDNI) return 8
            if (this.isRUC) return 11
            return 20
        },
        docExpectedLength() {
            if (this.isDNI) return 8
            if (this.isRUC) return 11
            return null
        },
        docLabel() {
            if (this.isDNI) return 'N° DNI (8 dígitos)'
            if (this.isRUC) return 'N° RUC (11 dígitos)'
            return 'N° documento'
        },
        docHint() {
            if (!this.formActivo.idTipoDocumento) return 'Seleccione tipo de documento primero'
            if (this.docExpectedLength && this.formActivo.numeroDocumento) {
                const len = (this.formActivo.numeroDocumento || '').length
                if (len < this.docExpectedLength) return `${len}/${this.docExpectedLength} dígitos`
                if (len === this.docExpectedLength) return '✓ Completo'
            }
            return ''
        },
        docLengthValid() {
            if (!this.docExpectedLength) return !!(this.formActivo.numeroDocumento && this.formActivo.numeroDocumento.length >= 3)
            return (this.formActivo.numeroDocumento || '').length === this.docExpectedLength
        },
        docValidationRule() {
            if (this.isDNI) return 'required|numeric|min:8|max:8'
            if (this.isRUC) return 'required|numeric|min:11|max:11'
            return 'required'
        }
    },
    watch: {
        value: {
            async handler(val) {
                if (val) {
                    this.clienteSeleccionadoId = val
                    if (!this.clienteActual || this.clienteActual.id !== val) {
                        await this.cargarCliente(val)
                    }
                } else {
                    this.onClear()
                }
            },
            immediate: true
        },
        searchText: debounce(function (val) {
            // Ignorar si el texto es igual al displayName del cliente seleccionado
            if (this.clienteActual && this.clienteActual.displayName === val) return
            if (!val || val.trim().length < 3) {
                this.searchResults = []
                return
            }
            this.performSearch(val)
        }, 500),
        'formActivo.numeroDocumento'(val) {
            // Auto-search when document number reaches expected length
            if (this.modoModal !== 'crear') return
            if (!val || !this.docExpectedLength) return
            if (val.length === this.docExpectedLength && this.formActivo.idTipoDocumento) {
                this.buscarDocumento()
            }
        },
        'formActivo.idTipoDocumento'() {
            // Only clear the document number when the user manually changes the type
            // in create mode. In edit mode this watcher fires when formActivo is
            // replaced with the existing client data, which would erase the number.
            if (this.modoModal === 'editar') return
            if (this.formActivo.numeroDocumento) {
                this.$set(this.formActivo, 'numeroDocumento', '')
            }
        }
    },
    methods: {
        soloNumeros(evt) {
            const char = String.fromCharCode(evt.which || evt.keyCode)
            if (!/\d/.test(char)) evt.preventDefault()
        },
        async performSearch(text) {
            this.isSearching = true
            try {
                const results = await this.$store.dispatch('clientes/searchClientes', {
                    text,
                    idSede: this.user.idSede
                })
                this.searchResults = results.map((c) => ({
                    ...c,
                    displayName: `${c.defaultName || c.name} (${c.number})`
                }))
            } catch (error) {
                console.error(error)
                this.searchResults = []
            } finally {
                this.isSearching = false
            }
        },
        async cargarCliente(id) {
            // Deduplicate: if already fetching this client, reuse the same promise
            if (this._cargarClientePromise && this._cargarClienteId === id) {
                return this._cargarClientePromise
            }
            this._cargarClienteId = id
            this._cargarClientePromise = this._doCargarCliente(id)
            try {
                return await this._cargarClientePromise
            } finally {
                this._cargarClientePromise = null
                this._cargarClienteId = null
            }
        },
        async _doCargarCliente(id) {
            try {
                this.isSearching = true
                const result = await this.$store.dispatch('clientes/getClienteById', id)
                if (result) {
                    this.clienteActual = {
                        ...result,
                        displayName: `${result.defaultName || result.name} (${result.number})`
                    }
                    this.actualizarStoreVentas(this.clienteActual)
                }
            } catch (error) {
                console.error(error)
            } finally {
                this.isSearching = false
            }
        },
        onClientSelect(id) {
            if (!id) {
                this.onClear()
                return
            }
            const selected = this.displayResults.find((r) => r.id === id)
            if (selected) {
                this.clienteActual = selected
                this.actualizarStoreVentas(selected)
                this.$emit('input', id)
                // emit full client object so parent can autofill without a second fetch
                this.$emit('client-selected', selected)
            }
        },
        onClear() {
            this.clienteSeleccionadoId = null
            this.clienteActual = null
            this.searchText = ''
            this.searchResults = []
            this.$emit('input', null)
            this.$store.commit('ventas/UPDATE_DATOS', {
                idCliente: null,
                numeroDocumento: null,
                emailClient: null,
                phoneClient: null,
                full_name: null,
                idTipoDocumento: null
            })
        },
        actualizarStoreVentas(cliente) {
            if (!cliente) return
            this.$store.commit('ventas/UPDATE_DATOS', {
                idCliente: cliente.id,
                numeroDocumento: cliente.number,
                emailClient: cliente.email,
                phoneClient: cliente.telephone,
                full_name: cliente.defaultName || cliente.name,
                idTipoDocumento: cliente.identity_document_type_id
            })
        },
        async abrirModal(modo) {
            this.modoModal = modo
            if (modo === 'crear') {
                this.formActivo = { ...DefaultForm.formClientesV2(), idSede: this.user.idSede }
                this.$validator.reset()
            } else if (modo === 'editar') {
                if (!this.clienteActual && this.clienteSeleccionadoId) {
                    await this.cargarCliente(this.clienteSeleccionadoId)
                }
                if (this.clienteActual) {
                    this.formActivo = {
                        idTipoDocumento: this.clienteActual.identity_document_type_id,
                        numeroDocumento: this.clienteActual.number,
                        full_name: this.clienteActual.defaultName || this.clienteActual.name,
                        telefono: this.clienteActual.telephone,
                        correo: this.clienteActual.email,
                        direccion: this.clienteActual.address,
                        isDistrito: this.clienteActual.district_id,
                        idSede: this.user.idSede
                    }
                }
            }
            // Esperar a que Vue procese los cambios en formActivo antes de mostrar
            // el dialog. Vuetify 2 no refleja el valor inicial en campos readonly/disabled
            // si formActivo y modalVisible cambian en el mismo tick.
            await this.$nextTick()
            this.modalVisible = true
        },
        /**
         * Abre el modal de crear cliente con datos pre-cargados desde la cotización.
         * Útil cuando el usuario llenó nombre/teléfono/correo en modo "sin cliente"
         * y quiere crear el cliente sin perder lo escrito.
         */
        abrirModalConDatos(datos = {}) {
            this.modoModal = 'crear'
            this.formActivo = {
                ...DefaultForm.formClientesV2(),
                idSede: this.user.idSede,
                full_name: datos.full_name || '',
                telefono: datos.telefono || '',
                correo: datos.correo || ''
            }
            this.$validator.reset()
            this.$nextTick(() => {
                this.modalVisible = true
            })
        },
        cerrarModal() {
            this.modalVisible = false
        },
        async buscarDocumento() {
            if (!this.formActivo.numeroDocumento || !this.formActivo.idTipoDocumento) {
                this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Ingrese documento y tipo', color: 'error', snackbar: true })
                return
            }
            // Validate length before calling API
            if (this.docExpectedLength && this.formActivo.numeroDocumento.length !== this.docExpectedLength) {
                this.$store.commit('mainUI/UPDATE_SNACKBAR', {
                    text: `El ${this.isDNI ? 'DNI' : 'RUC'} debe tener ${this.docExpectedLength} dígitos`,
                    color: 'warning',
                    snackbar: true
                })
                return
            }
            // RUC must start with 10 or 20
            if (this.isRUC && !/^(10|20)/.test(this.formActivo.numeroDocumento)) {
                this.$store.commit('mainUI/UPDATE_SNACKBAR', {
                    text: 'El RUC debe iniciar con 10 (persona natural) o 20 (persona jurídica)',
                    color: 'warning',
                    snackbar: true
                })
                return
            }
            try {
                this.buscandoDoc = true
                const res = await this.$store.dispatch('clientes/searchPersonByDoc', {
                    numDoc: this.formActivo.numeroDocumento,
                    idTipoDocumento: this.formActivo.idTipoDocumento,
                    idSede: this.user.idSede
                })

                if (res && res.success && res.data) {
                    // Populate form with fetched data — use $set for Vue 2 reactivity
                    this.$set(this.formActivo, 'full_name', res.data.name || this.formActivo.full_name)
                    this.$set(this.formActivo, 'direccion', res.data.address || '')

                    // Set ubigeo if available
                    if (res.data.district_id) {
                        this.$set(this.formActivo, 'isDistrito', res.data.district_id)
                    }

                    this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Datos encontrados correctamente', color: 'success', snackbar: true })
                } else {
                    this.$store.commit('mainUI/UPDATE_SNACKBAR', {
                        text: res?.error || 'No se encontraron datos para este documento',
                        color: 'warning',
                        snackbar: true
                    })
                }
            } catch (error) {
                console.error('Error searching document:', error)
                this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Error al buscar datos del documento', color: 'error', snackbar: true })
            } finally {
                this.buscandoDoc = false
            }
        },
        async guardarCliente() {
            const isValid = await this.$validator.validateAll()
            if (!isValid) return
            this.loadingSave = true
            try {
                const districtId = this.formActivo.isDistrito || null
                const payload = {
                    ...this.formActivo,
                    id: this.clienteActual.id,
                    numeroDocumento: this.formActivo.numeroDocumento,
                    // Send empty string instead of null so backend knows field was touched
                    telefono: this.formActivo.telefono || undefined,
                    correo: this.formActivo.correo || undefined,
                    direccion: this.formActivo.direccion || undefined,
                    district_id: districtId,
                    department_id: districtId ? districtId.slice(0, 2) : null,
                    province_id: districtId ? districtId.slice(0, 4) : null
                }
                await this.$store.dispatch('clientes/patchClient', payload)
                this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Cliente actualizado correctamente', color: 'success', snackbar: true })
                await this.cargarCliente(this.clienteActual.id)
                this.cerrarModal()
            } catch (error) {
                console.error(error)
                this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Error al actualizar', color: 'error', snackbar: true })
            } finally {
                this.loadingSave = false
            }
        },
        async guardarClienteNuevo() {
            const isValid = await this.$validator.validateAll()
            if (!isValid) return
            this.loadingSave = true
            try {
                const districtId = this.formActivo.isDistrito || null
                const payload = {
                    ...this.formActivo,
                    type: this.type,
                    district_id: districtId,
                    department_id: districtId ? districtId.slice(0, 2) : null,
                    province_id: districtId ? districtId.slice(0, 4) : null,
                    ubigeo: districtId
                }
                const response = await this.$store.dispatch('clientes/createClienteV2', payload)
                if (response && response.data && response.data.id) {
                    this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Cliente creado', color: 'success', snackbar: true })
                    this.$emit('input', response.data.id)
                    this.cerrarModal()
                }
            } catch (error) {
                console.error(error)
                this.$store.commit('mainUI/UPDATE_SNACKBAR', { text: 'Error al crear', color: 'error', snackbar: true })
            } finally {
                this.loadingSave = false
            }
        }
    }
}
</script>

<style scoped>
.text-muted {
    color: #6c757d;
}
</style>
